import { mockPackageHash, sumMoney, type MockRunData } from './mockDataset';
import { JURISDICTION_LEVELS, RATE_TYPES, ROLE, SINGLE_USE_ROLES, US_STATE_CODES } from './mappingRoles';
import type {
  ColumnAssignment, ColumnMappingConfig, DetectedType, Issue, MappingSuggestion, MappingTarget, PreviewRun, Statement,
  TabularColumn,
} from './types';

/**
 * In-browser stand-in for the server's guided column mapping (EPN.PayrollReview.Api Template/MappingSuggester.cs
 * and GenericMappedPayrollHistoryImportAdapter.cs). MOCK ONLY: it exists so the mapping workflow is demonstrable
 * end to end with no backend. It follows the same conservative rules (EPN suggests, the person confirms, and the
 * dangerous swaps are blocking errors), and, like the server, it NEVER returns a cell value: only headers, a
 * detected type per column, counts, and findings that name a column. All sample data is synthetic.
 */

// ---------------------------------------------------------------- reading a CSV

const MAX_ROWS = 5000;

/** Splits CSV text into records (RFC 4180 style: quotes, doubled quotes, CRLF or LF, a leading BOM). */
export function parseCsv(input: string): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const records: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; } else { quoted = false; }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field); field = '';
      records.push(row); row = [];
    } else {
      field += ch;
    }
  }
  if (field !== '' || row.length > 0) { row.push(field); records.push(row); }
  return records;
}

export interface ParsedTable {
  headers: string[];
  /** Data rows with the row number a person sees (the header is row 1). Kept in memory by the mock only. */
  rows: { rowNumber: number; cells: (string | null)[] }[];
}

/** Null when the file has no usable header row. */
export function tableFromCsv(text: string): ParsedTable | null {
  const records = parseCsv(text);
  const headerRecord = records[0];
  if (!headerRecord || headerRecord.every((h) => h.trim() === '')) return null;
  const headers = headerRecord.map((h) => h.trim());
  const rows = records.slice(1)
    .map((cells, i) => ({ rowNumber: i + 2, cells: cells.map((c) => (c.trim() === '' ? null : c.trim())) }))
    .filter((r) => r.cells.some((c) => c !== null))
    .slice(0, MAX_ROWS);
  return { headers, rows };
}

// ---------------------------------------------------------------- plain-text values

const NUMBER = /^\$?(\d{1,3}(,\d{3})+|\d+)(\.\d+)?$/;

/** A plain, non-negative number (optionally with $ and thousands commas) as a plain decimal string, else null. */
export function tryNumber(value: string): string | null {
  const v = value.trim();
  if (v.length === 0 || v.length > 24 || !NUMBER.test(v)) return null;
  return v.replace(/[$,]/g, '');
}

/** yyyy-MM-dd or US month/day/year (M/d/yyyy, M/d/yy) as yyyy-MM-dd. Impossible dates are refused. */
export function tryDate(value: string): string | null {
  const v = value.trim();
  let y: number; let m: number; let d: number;
  let match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (match) {
    y = Number(match[1]); m = Number(match[2]); d = Number(match[3]);
  } else {
    match = /^(\d{1,2})\/(\d{1,2})\/(\d{4}|\d{2})$/.exec(v);
    if (!match) return null;
    m = Number(match[1]); d = Number(match[2]); y = Number(match[3]);
    if (match[3]!.length === 2) y += 2000;
  }
  const date = new Date(Date.UTC(y, m - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== m - 1 || date.getUTCDate() !== d) return null;
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function detectType(values: string[]): DetectedType {
  if (values.length === 0) return 'Empty';
  if (values.every((v) => tryNumber(v) !== null)) return 'Number';
  if (values.every((v) => tryDate(v) !== null)) return 'Date';
  if (values.every((v) => ['yes', 'no', 'y', 'n', 'true', 'false'].includes(v.toLowerCase()))) return 'YesNo';
  return 'Text';
}

/** Header + detected type per column. The values themselves never leave this function. */
export function describeColumns(table: ParsedTable): TabularColumn[] {
  return table.headers.map((header, index) => {
    const values = table.rows.map((r) => r.cells[index] ?? null).filter((v): v is string => v !== null).slice(0, 200);
    return { index, header, detectedType: detectType(values) };
  });
}

/** Stand-in for the server's SHA-256 layout fingerprint: same format + same ordered headers => same value. */
export function fingerprintOf(format: string, headers: string[]): string {
  return mockPackageHash(`${format}\n${headers.map(normalizeHeader).join('\u001f')}`);
}

// ---------------------------------------------------------------- header rules (port of MappingSuggester)

export const normalizeHeader = (header: string): string => header.toLowerCase().replace(/[^\p{L}\p{N}]/gu, '');

const EMPLOYEE_ID = ['employeeid', 'empid', 'employeenumber', 'employeeno', 'employeenum', 'workerid', 'associateid', 'personid', 'payrollid', 'staffid'];
const NAME = ['name', 'employeename', 'fullname', 'employee', 'associatename', 'workername'];
const FIRST_NAME = ['firstname', 'first', 'givenname'];
const LAST_NAME = ['lastname', 'last', 'surname', 'familyname'];
const RUN_ID = ['payrollrunid', 'runid', 'payrunid', 'batchid', 'payrollbatch', 'payrollnumber'];
const PAY_DATE = ['paydate', 'checkdate', 'paymentdate', 'datepaid', 'payday', 'paiddate', 'issuedate'];
const PERIOD_START = ['periodstart', 'payperiodstart', 'periodbegin', 'payperiodbegin', 'periodstartdate', 'payperiodfrom', 'periodfrom', 'startdate', 'beginningdate'];
const PERIOD_END = ['periodend', 'payperiodend', 'periodenddate', 'payperiodto', 'periodto', 'enddate', 'endingdate'];
const PAYROLL_TYPE = ['payrolltype', 'runtype', 'checktype', 'paytype'];
const GROSS = ['grosspay', 'grossearnings', 'gross', 'totalgross', 'grosswages', 'totalearnings', 'grossamount'];
const NET = ['netpay', 'net', 'takehomepay', 'takehome', 'netamount', 'netcheck', 'checkamount'];
const TOTAL_TAX = ['totaltaxes', 'totalemployeetaxes', 'employeetaxestotal', 'taxestotal', 'totalwithholding'];
const TOTAL_DEDUCTION = ['totaldeductions', 'deductionstotal', 'totalemployeedeductions'];
const PAYMENT_METHOD = ['paymentmethod', 'paidby', 'paymenttype', 'method', 'deliverymethod'];
const PAYMENT_REFERENCE = ['checknumber', 'checkno', 'checknum', 'paymentreference', 'reference', 'documentnumber'];

const SENSITIVE = ['ssn', 'socialsecuritynumber', 'taxpayerid', 'accountnumber', 'routing', 'dateofbirth', 'dob', 'bankaccount', 'iban'];
const NOT_PAYROLL = ['jobtitle', 'title', 'position', 'department', 'dept', 'location', 'division', 'status', 'hiredate', 'terminationdate', 'address', 'phone', 'email', 'gender', 'notes', 'comment', 'costcenter', 'jobcode'];
const TAX_WORDS = ['tax', 'fica', 'futa', 'suta', 'sui', 'withholding', 'socialsecurity', 'oasdi', 'medicare', 'sdi', 'fit', 'sit', 'unemployment', 'wht'];
const DEDUCTION_WORDS = ['401k', '403b', 'roth', 'insurance', 'health', 'dental', 'vision', 'hsa', 'fsa', 'garnish', 'deduction', 'retirement', 'medical', 'union', 'loan', 'child', 'support', 'life', 'disability', 'ira', 'pension', 'advance', 'uniform', 'parking', 'transit', 'levy'];
const REIMBURSEMENT_WORDS = ['reimburs', 'mileage', 'expense', 'allowance'];
const EARNING_WORDS = ['regular', 'overtime', 'doubletime', 'bonus', 'commission', 'salary', 'wages', 'holiday', 'vacation', 'pto', 'sick', 'tips', 'earning', 'hourlypay', 'stipend', 'severance', 'retro', 'piece', 'paidtimeoff', 'personal', 'bereavement', 'juryduty'];
const FEDERAL_WORDS = ['federal', 'fit', 'fica', 'socialsecurity', 'oasdi', 'medicare', 'futa', 'fed'];
const STATE_WORDS = ['state', 'sdi', 'sit', 'suta', 'sui', 'disability', 'familyleave', 'paidfamily'];
const LOCAL_WORDS = ['city', 'local', 'county', 'municipal', 'school'];

const containsAny = (n: string, words: string[]): boolean => words.some((w) => n.includes(w));

export function isSensitiveHeader(header: string): boolean {
  return containsAny(normalizeHeader(header), SENSITIVE);
}

export function looksLikeJobTitleOrOtherInfo(header: string): boolean {
  const n = normalizeHeader(header);
  return NOT_PAYROLL.some((f) => n === f || n.startsWith(f) || n.endsWith(f));
}

function looksLikeEmployerNormalized(n: string, header: string): boolean {
  return n.startsWith('employer') || n.startsWith('company') || n.includes('employerpaid') || n.includes('companypaid')
    || n.includes('employershare') || header.toUpperCase().includes('(ER)') || header.startsWith('ER ');
}

export const looksLikeEmployer = (header: string): boolean => looksLikeEmployerNormalized(normalizeHeader(header), header);
export const looksLikeReimbursement = (header: string): boolean => containsAny(normalizeHeader(header), REIMBURSEMENT_WORDS);
export const looksLikePayDate = (header: string): boolean => PAY_DATE.includes(normalizeHeader(header));

export function looksLikeName(header: string): boolean {
  const n = normalizeHeader(header);
  return NAME.includes(n) || FIRST_NAME.includes(n) || LAST_NAME.includes(n) || n.endsWith('name');
}

function levelFor(n: string): string {
  if (containsAny(n, LOCAL_WORDS)) return 'Local';
  if (containsAny(n, STATE_WORDS)) return 'State';
  if (containsAny(n, FEDERAL_WORDS)) return 'Federal';
  return 'Other';
}

type SuggestionExtras = Partial<Pick<MappingSuggestion, 'jurisdictionLevel' | 'rateType' | 'taxable'>>;

function suggestOne(column: TabularColumn): MappingSuggestion | null {
  const header = column.header;
  const n = normalizeHeader(header);
  if (n.length === 0) return null;
  const S = (role: string, reason: string, confidence: string, extras: SuggestionExtras = {}): MappingSuggestion =>
    ({ sourceColumn: header, role, reason, confidence, ...extras });

  if (containsAny(n, SENSITIVE)) return S(ROLE.Ignore, 'This looks like a sensitive identifier or bank detail. It is never imported.', 'High');

  if (EMPLOYEE_ID.includes(n) && column.detectedType !== 'Date') return S(ROLE.EmployeeId, "Looks like the employee's ID in your old system.", 'High');
  if (FIRST_NAME.includes(n)) return S(ROLE.FirstName, "Looks like the employee's first name.", 'High');
  if (LAST_NAME.includes(n)) return S(ROLE.LastName, "Looks like the employee's last name.", 'High');
  if (NAME.includes(n)) return S(ROLE.FullName, "Looks like the employee's full name. It is shown for review only and is never used to match employees.", 'Medium');
  if (RUN_ID.includes(n)) return S(ROLE.RunId, "Looks like the payroll run's ID.", 'Medium');
  if (PAY_DATE.includes(n)) return S(ROLE.PayDate, 'Looks like the date employees were paid.', 'High');
  if (PERIOD_START.includes(n)) return S(ROLE.PeriodStart, 'Looks like the first day of the pay period.', n === 'startdate' ? 'Low' : 'High');
  if (PERIOD_END.includes(n)) return S(ROLE.PeriodEnd, 'Looks like the last day of the pay period (not the pay date).', n === 'enddate' ? 'Low' : 'High');
  if (PAYROLL_TYPE.includes(n)) return S(ROLE.PayrollType, 'Looks like the kind of payroll (regular, bonus, off-cycle).', 'Low');

  if (GROSS.includes(n)) return S(ROLE.StatedGross, 'A total the file states. EPN uses it only to double-check the sum of your earnings.', 'Medium');
  if (NET.includes(n)) return S(ROLE.StatedNet, 'A total the file states. EPN uses it only to double-check take-home pay.', 'Medium');
  if (TOTAL_TAX.includes(n)) return S(ROLE.StatedEmployeeTaxes, 'A total the file states. EPN uses it only to double-check the taxes.', 'Medium');
  if (TOTAL_DEDUCTION.includes(n)) return S(ROLE.StatedDeductions, 'A total the file states. EPN uses it only to double-check the deductions.', 'Medium');
  if (PAYMENT_METHOD.includes(n)) return S(ROLE.PaymentMethod, 'Looks like how the employee was paid.', 'Medium');
  if (PAYMENT_REFERENCE.includes(n)) return S(ROLE.PaymentReference, 'Looks like a check or payment number. Only the last four characters are kept.', 'Medium');

  if (NOT_PAYROLL.some((f) => n === f || n.startsWith(f) || n.endsWith(f))) {
    return S(ROLE.Ignore, 'Not needed for payroll history. (A job title is never treated as a pay type.)', 'Medium');
  }

  if (column.detectedType === 'Text' || column.detectedType === 'Date' || column.detectedType === 'Empty') return null;

  const employer = looksLikeEmployerNormalized(n, header);
  if (employer && containsAny(n, TAX_WORDS)) {
    return S(ROLE.EmployerTax, 'Looks like a tax paid by the company. It will not reduce take-home pay.', 'Medium', { jurisdictionLevel: levelFor(n) });
  }
  if (employer) return S(ROLE.EmployerContribution, 'Looks like a benefit paid by the company. It will not reduce take-home pay.', 'Medium');

  if (n.includes('hours') || n === 'hrs' || n.endsWith('hrs')) return S(ROLE.Hours, 'Looks like hours worked. Choose which earning these hours belong to.', 'Medium');
  if (containsAny(n, REIMBURSEMENT_WORDS)) {
    return S(ROLE.Reimbursement, 'Looks like money paid back for expenses. It is kept separate from wages and is not taxable.', 'Medium', { taxable: false });
  }
  if (containsAny(n, TAX_WORDS) && !n.includes('deduction')) {
    return S(ROLE.EmployeeTax, "Looks like a tax taken from the employee's pay.", 'Medium', { jurisdictionLevel: levelFor(n) });
  }
  if (containsAny(n, DEDUCTION_WORDS)) {
    return S(ROLE.DeductionNeedsClassification, "Looks like money taken from the employee's pay. You must choose whether it is taken before tax or after tax.", 'Medium');
  }
  if (containsAny(n, EARNING_WORDS)) {
    return S(ROLE.Earning, 'Looks like money the employee earned.', 'Medium', {
      rateType: n.includes('overtime') ? 'Overtime' : n.includes('doubletime') ? 'DoubleTime' : 'Regular', taxable: true,
    });
  }
  return null;
}

/** EPN's guesses from the headers alone. A single-use role is suggested for one column only. */
export function suggestMappings(columns: TabularColumn[]): MappingSuggestion[] {
  const used = new Set<string>();
  const list: MappingSuggestion[] = [];
  for (const column of columns) {
    const suggestion = suggestOne(column);
    if (!suggestion) continue;
    if (SINGLE_USE_ROLES.has(suggestion.role) && used.has(suggestion.role)) continue;
    if (SINGLE_USE_ROLES.has(suggestion.role)) used.add(suggestion.role);
    list.push(suggestion);
  }
  return list;
}

// ---------------------------------------------------------------- the choices (mirror of MappingTargetCatalog)

const T = (role: string, label: string, help: string, group: string, singleUse: boolean, needsTaxDetail = false, needsEarningLink = false): MappingTarget =>
  ({ role, label, help, group, singleUse, needsTaxDetail, needsEarningLink });

export const MOCK_MAPPING_TARGETS: MappingTarget[] = [
  T(ROLE.EmployeeId, 'Employee ID', "The employee's ID in your old system. Never a name.", 'Who and when', true),
  T(ROLE.FirstName, 'First name', 'Used for display and review only.', 'Who and when', true),
  T(ROLE.LastName, 'Last name', 'Used for display and review only.', 'Who and when', true),
  T(ROLE.FullName, 'Full name', 'Use this when first and last name share one column.', 'Who and when', true),
  T(ROLE.RunId, 'Payroll run ID (optional)', 'If blank, EPN groups rows by pay date and pay period.', 'Who and when', true),
  T(ROLE.PayDate, 'Pay date', 'The date employees were paid.', 'Who and when', true),
  T(ROLE.PeriodStart, 'Pay period start', 'First day of the pay period.', 'Who and when', true),
  T(ROLE.PeriodEnd, 'Pay period end', 'Last day of the pay period. Not the pay date.', 'Who and when', true),
  T(ROLE.PayrollType, 'Payroll type (optional)', 'Regular, bonus, off-cycle and so on.', 'Who and when', true),
  T(ROLE.Earning, 'Earning (money employees earned)', 'Wages, overtime, bonus and similar. Added up as gross pay.', 'Money', false),
  T(ROLE.Reimbursement, 'Reimbursement', 'Expenses paid back. Kept separate from wages and not taxable.', 'Money', false),
  T(ROLE.EmployeeTax, 'Employee tax (taken from pay)', 'Reduces take-home pay.', 'Money', false, true),
  T(ROLE.EmployerTax, 'Employer tax (paid by the company)', 'Does not reduce take-home pay.', 'Money', false, true),
  T(ROLE.PreTaxDeduction, 'Deduction taken before tax', 'Reduces take-home pay.', 'Money', false),
  T(ROLE.PostTaxDeduction, 'Deduction taken after tax', 'Reduces take-home pay.', 'Money', false),
  T(ROLE.EmployerContribution, 'Employer contribution (paid by the company)', 'For example a 401(k) match. Does not reduce take-home pay.', 'Money', false),
  T(ROLE.Hours, 'Hours worked', 'Choose which earning column these hours belong to.', 'Money', false, false, true),
  T(ROLE.StatedGross, 'Total gross pay (to double-check)', 'A total your file states. Used only to check EPN\'s sums.', 'Totals your file states', true),
  T(ROLE.StatedEmployeeTaxes, 'Total employee taxes (to double-check)', "Used only to check EPN's sums.", 'Totals your file states', true),
  T(ROLE.StatedDeductions, 'Total deductions (to double-check)', "Used only to check EPN's sums.", 'Totals your file states', true),
  T(ROLE.StatedNet, 'Net pay (take-home) (to double-check)', 'Strongly recommended: lets EPN confirm the math.', 'Totals your file states', true),
  T(ROLE.PaymentMethod, 'Payment method (optional)', 'Check, direct deposit and so on.', 'Payment', true),
  T(ROLE.PaymentReference, 'Check or payment number (optional)', 'Only the last four characters are kept.', 'Payment', true),
  T(ROLE.Ignore, 'Do not import this column', 'Use for job titles, departments, IDs or anything not needed.', 'Other', false),
];

// ---------------------------------------------------------------- validating a confirmed mapping

const LINE_ROLES = new Set<string>([
  ROLE.Earning, ROLE.Reimbursement, ROLE.EmployeeTax, ROLE.EmployerTax, ROLE.PreTaxDeduction, ROLE.PostTaxDeduction, ROLE.EmployerContribution,
]);
const KNOWN_ROLES = new Set<string>(Object.values(ROLE).filter((r) => r !== ROLE.DeductionNeedsClassification));
const trunc = (s: string): string => (s.length > 60 ? s.slice(0, 60) : s);

function error(code: string, message: string, file: string | null, column?: string, rowNumber: number | null = null, sourceRecordId: string | null = null): Issue {
  return { severity: 'BlockingError', code, message: column === undefined ? message : `${message} (column '${trunc(column)}')`, file, rowNumber, sourceRecordId };
}

function checkConflicts(c: ColumnAssignment, column: string, push: (code: string, message: string) => void): void {
  const employeeSide = [ROLE.EmployeeTax, ROLE.PreTaxDeduction, ROLE.PostTaxDeduction, ROLE.StatedEmployeeTaxes, ROLE.StatedDeductions].includes(c.role as never);
  const money = LINE_ROLES.has(c.role) || [ROLE.StatedGross, ROLE.StatedNet, ROLE.StatedEmployeeTaxes, ROLE.StatedDeductions].includes(c.role as never);
  if (employeeSide && looksLikeEmployer(column)) {
    push('MAPPING_ROLE_CONFLICT', "This column looks like something the company pays, but it was chosen as something taken from the employee's pay. Choose Employer tax or Employer contribution instead");
  }
  if (money && looksLikeJobTitleOrOtherInfo(column)) {
    push('MAPPING_ROLE_CONFLICT', 'This column looks like a job title or other description, not an amount of money');
  }
  if (c.role === ROLE.EmployeeId && looksLikeName(column)) {
    push('MAPPING_ROLE_CONFLICT', "This column looks like a name. Employees are never identified by name; choose the column with the employee's ID");
  }
  if (c.role === ROLE.PeriodEnd && looksLikePayDate(column)) {
    push('MAPPING_ROLE_CONFLICT', 'This column looks like the pay date, not the end of the pay period. The two are different dates and are never swapped');
  }
  if (c.role === ROLE.PeriodStart && looksLikePayDate(column)) {
    push('MAPPING_ROLE_CONFLICT', 'This column looks like the pay date, not the start of the pay period');
  }
  if (c.role === ROLE.Earning && looksLikeReimbursement(column)) {
    push('MAPPING_ROLE_CONFLICT', 'This column looks like a reimbursement. Reimbursements are kept separate from wages and are never treated as taxable earnings');
  }
  if (c.role === ROLE.Reimbursement && c.taxable === true) {
    push('MAPPING_ROLE_CONFLICT', 'Reimbursements are not wages; they cannot be marked taxable here');
  }
}

/** Checks a confirmed mapping against the headers and the safety rules (no dangerous swaps). Header names only. */
export function validateMapping(headers: string[], mapping: ColumnMappingConfig, fileLabel: string | null): Issue[] {
  const f: Issue[] = [];
  const err = (code: string, message: string, column?: string) => f.push(error(code, message, fileLabel, column));

  if (mapping.version !== 1) {
    err('MAPPING_VERSION_UNSUPPORTED', 'This column mapping was saved in an older format. Map the columns again.');
    return f;
  }

  const trimmed = headers.map((h) => h.trim());
  const counts = new Map<string, number>();
  for (const h of trimmed.filter((h) => h.length > 0)) counts.set(h.toLowerCase(), (counts.get(h.toLowerCase()) ?? 0) + 1);
  for (const h of trimmed) {
    if ((counts.get(h.toLowerCase()) ?? 0) > 1) {
      err('MAPPING_DUPLICATE_HEADER', 'Two columns have the same heading, so they cannot be told apart. Rename one in your file', h);
      counts.set(h.toLowerCase(), 0);
    }
  }

  const headerSet = new Set(trimmed.map((h) => h.toLowerCase()));
  const seen = new Set<string>();
  const roleCounts = new Map<string, number>();
  const earningHeaders = new Set(mapping.columns.filter((c) => c.role === ROLE.Earning).map((c) => c.sourceColumn.trim().toLowerCase()));

  for (const c of mapping.columns) {
    const column = c.sourceColumn.trim();
    if (!headerSet.has(column.toLowerCase())) { err('MAPPING_COLUMN_NOT_FOUND', 'A mapped column is not in the file', column); continue; }
    if (seen.has(column.toLowerCase())) { err('MAPPING_DUPLICATE_COLUMN', 'A column can only have one meaning', column); continue; }
    seen.add(column.toLowerCase());
    if (!KNOWN_ROLES.has(c.role)) {
      err('MAPPING_ROLE_UNKNOWN', c.role === ROLE.DeductionNeedsClassification
        ? 'Choose whether this deduction is taken before tax (pre-tax) or after tax (post-tax); EPN never guesses'
        : 'A column has a meaning EPN does not recognize', column);
      continue;
    }
    roleCounts.set(c.role, (roleCounts.get(c.role) ?? 0) + 1);
    if (c.role === ROLE.Ignore) continue;

    if (isSensitiveHeader(column)) {
      err('SENSITIVE_COLUMN_MAPPED', 'This column looks like a sensitive identifier or bank detail and cannot be imported. Set it to Ignore', column);
    }
    checkConflicts(c, column, (code, message) => err(code, message, column));

    if (c.role === ROLE.EmployeeTax || c.role === ROLE.EmployerTax) {
      const level = c.jurisdictionLevel ?? 'Other';
      if (!JURISDICTION_LEVELS.some((l) => l.toLowerCase() === level.toLowerCase())) {
        err('INVALID_VALUE', 'The tax level must be Federal, State, Local or Other', column);
      } else if (level.toLowerCase() === 'state' && (!c.stateCode || !US_STATE_CODES.has(c.stateCode.toUpperCase()))) {
        err('INVALID_STATE_CODE', 'A State tax needs a recognized two-letter state code', column);
      }
    }
    if (c.role === ROLE.Earning && c.rateType && !RATE_TYPES.some((r) => r.toLowerCase() === c.rateType!.toLowerCase())) {
      err('INVALID_VALUE', 'The pay type must be Regular, Overtime, DoubleTime or Other', column);
    }
    if (c.role === ROLE.Hours && (!c.code || !earningHeaders.has(c.code.trim().toLowerCase()))) {
      err('MAPPING_HOURS_TARGET_MISSING', 'Choose which earning column these hours belong to', column);
    }
  }

  for (const role of SINGLE_USE_ROLES) {
    if ((roleCounts.get(role) ?? 0) > 1) err('MAPPING_ROLE_DUPLICATE', `More than one column was chosen as '${role}'. Only one column can have this meaning`);
  }
  const n = (role: string) => roleCounts.get(role) ?? 0;
  if (n(ROLE.EmployeeId) === 0) err('MAPPING_REQUIRED_ROLE_MISSING', "Choose the column that holds each employee's ID in your old system. Names are never used to identify employees");
  if (n(ROLE.PayDate) === 0) err('MAPPING_REQUIRED_ROLE_MISSING', 'Choose the column that holds the pay date');
  if (n(ROLE.PeriodStart) === 0) err('MAPPING_REQUIRED_ROLE_MISSING', 'Choose the column that holds the pay period start date');
  if (n(ROLE.PeriodEnd) === 0) err('MAPPING_REQUIRED_ROLE_MISSING', 'Choose the column that holds the pay period end date. It is never taken from the pay date');
  if (n(ROLE.FullName) === 0 && !(n(ROLE.FirstName) > 0 && n(ROLE.LastName) > 0)) {
    err('MAPPING_REQUIRED_ROLE_MISSING', "Choose the column(s) that hold the employee's name (a full name, or first and last name)");
  }
  if (n(ROLE.Earning) === 0) err('MAPPING_REQUIRED_ROLE_MISSING', 'Choose at least one column that holds money the employee earned');
  return f;
}

// ---------------------------------------------------------------- building the history from a confirmed mapping

export interface GuidedEmployee {
  sourceEmployeeId: string;
  displayName: string;
}

export interface GuidedBuild {
  employees: GuidedEmployee[];
  /** Runs with statements that are not yet matched to EPN employees (see {@link guidedRuns}). */
  runs: MockRunData[];
  /** Everything found while reading the rows (blocking errors, warnings, information). Never contains cell values. */
  issues: Issue[];
  earningCodes: string[];
  deductionCodes: string[];
  taxJurisdictions: string[];
}

const cents = (amount: number): number => Math.round(amount * 100);

function nameOf(row: { cells: (string | null)[] }, col: Record<string, number>): string {
  const cell = (role: string): string | null => (col[role] === undefined ? null : (row.cells[col[role]!] ?? null));
  const first = cell(ROLE.FirstName);
  const last = cell(ROLE.LastName);
  if (first && last) return `${first} ${last}`;
  const full = cell(ROLE.FullName);
  if (!full) return first ?? last ?? '(no name)';
  if (full.includes(',')) {
    const [l, f] = full.split(',', 2).map((s) => s.trim());
    return [f, l].filter(Boolean).join(' ');
  }
  return full;
}

function maskReference(value: string | null): string | null {
  if (value === null) return null;
  const alnum = value.replace(/[^\p{L}\p{N}]/gu, '');
  const tail = alnum.length <= 4 ? alnum : alnum.slice(-4);
  return /^\d*$/.test(tail) ? `****${tail}` : '****';
}

const METHODS: Record<string, string> = {
  check: 'Check', chk: 'Check', papercheck: 'Check', manualcheck: 'Check',
  directdeposit: 'Direct deposit', dd: 'Direct deposit', ach: 'Direct deposit', eft: 'Direct deposit', deposit: 'Direct deposit',
  cash: 'Cash', paycard: 'Pay card', card: 'Pay card',
};

/** Reads every row with the confirmed mapping (one row per employee per pay run) and reconciles each statement. */
export function buildGuided(table: ParsedTable, mapping: ColumnMappingConfig, fileLabel: string): GuidedBuild {
  const headers = table.headers.map((h) => h.trim());
  const indexOf = (column: string): number => headers.findIndex((h) => h.toLowerCase() === column.trim().toLowerCase());
  const issues: Issue[] = [];
  const single: Record<string, number> = {};
  const lines: { a: ColumnAssignment; col: number }[] = [];
  const hoursFor = new Map<string, number>();
  for (const a of mapping.columns.filter((x) => x.role !== ROLE.Ignore)) {
    const col = indexOf(a.sourceColumn);
    if (col < 0) continue;
    if (SINGLE_USE_ROLES.has(a.role)) single[a.role] = col;
    else if (a.role === ROLE.Hours && a.code) hoursFor.set(a.code.trim().toLowerCase(), col);
    else if (LINE_ROLES.has(a.role)) lines.push({ a, col });
  }
  const cell = (row: { cells: (string | null)[] }, col: number | undefined): string | null => (col === undefined ? null : (row.cells[col] ?? null));
  const rowError = (code: string, message: string, rowNumber: number, record: string | null = null): void => {
    issues.push(error(code, message, fileLabel, undefined, rowNumber, record));
  };

  const dateOf = (row: { rowNumber: number; cells: (string | null)[] }, role: string, label: string): string | null => {
    const raw = cell(row, single[role]);
    if (raw === null) { rowError('MISSING_REQUIRED_FIELD', `The ${label} is blank in this row.`, row.rowNumber); return null; }
    const iso = tryDate(raw);
    if (iso) return iso;
    rowError('INVALID_DATE', `The ${label} must be a date such as 2025-09-25 or 9/25/2025 (month/day/year).`, row.rowNumber);
    return null;
  };

  interface RunAcc { id: string; start: string; end: string; pay: string; statements: Statement[] }
  const runs = new Map<string, RunAcc>();
  const employees = new Map<string, GuidedEmployee>();
  const statementKeys = new Set<string>();
  const earningCodes = new Set<string>();
  const deductionCodes = new Set<string>();
  const jurisdictions = new Set<string>();
  const statedNetMapped = single[ROLE.StatedNet] !== undefined;
  let skipped = 0;

  for (const row of table.rows) {
    const employeeId = cell(row, single[ROLE.EmployeeId]);
    if (employeeId === null) { skipped++; continue; }
    const pay = dateOf(row, ROLE.PayDate, 'pay date');
    const start = dateOf(row, ROLE.PeriodStart, 'pay period start');
    const end = dateOf(row, ROLE.PeriodEnd, 'pay period end');
    if (pay === null || start === null || end === null) continue;

    const runId = cell(row, single[ROLE.RunId]) ?? `RUN-${pay}-${start}-${end}`;
    let run = runs.get(runId);
    if (!run) { run = { id: runId, start, end, pay, statements: [] }; runs.set(runId, run); }
    else if (run.start !== start || run.end !== end || run.pay !== pay) {
      rowError('RUN_DATES_INCONSISTENT', 'Rows with the same payroll run ID have different dates.', row.rowNumber, runId);
      continue;
    }
    const key = `${runId}|${employeeId}`;
    if (statementKeys.has(key)) {
      rowError('DUPLICATE_EMPLOYEE_RUN_ROW',
        'This employee appears more than once in the same pay run. A guided-mapping file needs one row per employee per pay run; use the EPN template for anything more detailed.',
        row.rowNumber, employeeId);
      continue;
    }
    statementKeys.add(key);
    if (!employees.has(employeeId)) employees.set(employeeId, { sourceEmployeeId: employeeId, displayName: nameOf(row, single) });

    const num = (header: string, raw: string | null): number | null => {
      if (raw === null) return null;
      const n = tryNumber(raw);
      if (n !== null) return Number(n);
      rowError('INVALID_DECIMAL',
        `The value in column '${trunc(header)}' must be a plain, non-negative number (negative amounts are not supported here; use the EPN template to record voids and corrections).`, row.rowNumber);
      return null;
    };

    const earnings: Statement['earnings'] = [];
    const deductions: Statement['deductions'] = [];
    const taxes: Statement['taxes'] = [];
    let reimbursed = 0;
    for (const { a, col } of lines) {
      const header = headers[col]!;
      const amount = num(header, cell(row, col));
      if (amount === null || amount === 0) continue;
      const lineId = `${row.rowNumber}-${col}`;
      const code = a.code ?? header;
      const name = a.label ?? header;
      switch (a.role) {
        case ROLE.Earning: {
          const hoursCol = hoursFor.get(header.toLowerCase());
          earnings.push({
            sourceLineId: `E${lineId}`, code, category: a.rateType ?? 'Regular',
            hours: hoursCol === undefined ? null : num(headers[hoursCol]!, cell(row, hoursCol)), rate: null, amount, isTaxable: a.taxable !== false,
          });
          earningCodes.add(code);
          break;
        }
        case ROLE.Reimbursement: reimbursed += amount; break;
        case ROLE.EmployeeTax:
        case ROLE.EmployerTax: {
          const level = a.jurisdictionLevel ?? 'Other';
          const jurisdiction = level.toLowerCase() === 'state' && a.stateCode ? a.stateCode.toUpperCase() : level;
          jurisdictions.add(jurisdiction);
          taxes.push({
            sourceLineId: `T${lineId}`, name, jurisdiction, scope: a.role === ROLE.EmployerTax ? 'Employer' : 'Employee',
            taxableWageBase: null, amount,
          });
          break;
        }
        case ROLE.PreTaxDeduction:
        case ROLE.PostTaxDeduction:
          deductionCodes.add(code);
          deductions.push({
            sourceLineId: `D${lineId}`, code, classification: a.role === ROLE.PreTaxDeduction ? 'PreTax' : 'PostTax', employeeAmount: amount, employerAmount: 0,
          });
          break;
        default: break; // Employer contributions are kept out of the pay statement view in the mock.
      }
    }

    const gross = sumMoney(earnings.map((e) => e.amount));
    const employeeTaxes = sumMoney(taxes.filter((t) => t.scope === 'Employee').map((t) => t.amount));
    const employerTaxes = sumMoney(taxes.filter((t) => t.scope === 'Employer').map((t) => t.amount));
    const pre = sumMoney(deductions.filter((d) => d.classification === 'PreTax').map((d) => d.employeeAmount));
    const post = sumMoney(deductions.filter((d) => d.classification === 'PostTax').map((d) => d.employeeAmount));
    const taxableEarnings = sumMoney(earnings.filter((e) => e.isTaxable).map((e) => e.amount));
    const derivedNet = (cents(gross) + cents(reimbursed) - cents(employeeTaxes) - cents(pre) - cents(post)) / 100;

    const stated = (role: string, derived: number): number => {
      const raw = cell(row, single[role]);
      const n = raw === null ? null : tryNumber(raw);
      return n === null ? derived : Number(n);
    };
    const checks: [string, number, number][] = [
      [ROLE.StatedNet, stated(ROLE.StatedNet, derivedNet), derivedNet],
      [ROLE.StatedGross, stated(ROLE.StatedGross, gross), gross],
      [ROLE.StatedEmployeeTaxes, stated(ROLE.StatedEmployeeTaxes, employeeTaxes), employeeTaxes],
      [ROLE.StatedDeductions, stated(ROLE.StatedDeductions, pre + post), pre + post],
    ];
    if (checks.some(([, s, d]) => Math.abs(cents(s) - cents(d)) > 1)) {
      rowError('UNBALANCED_PAY_STATEMENT',
        'This pay statement does not balance: a total your file states differs from the earnings, taxes and deductions that were mapped. Check the column choices and the file.',
        row.rowNumber, employeeId);
    }

    const methodRaw = cell(row, single[ROLE.PaymentMethod]);
    run.statements.push({
      sourcePayStatementId: `PS-${run.statements.length + 1}-${employeeId}`, sourceEmployeeId: employeeId,
      employeeDisplayName: employees.get(employeeId)!.displayName, epnEmployeeId: null, statementStatus: 'Original', correctsSourcePayStatementId: null,
      grossPay: gross, taxableWages: sumMoney([taxableEarnings, -pre]), preTaxDeductions: pre, postTaxDeductions: post, employeeTaxes, employerTaxes,
      reimbursements: reimbursed, netPay: derivedNet,
      paymentMethodDescription: methodRaw === null ? null : (METHODS[normalizeHeader(methodRaw)] ?? 'Other'),
      paymentReference: maskReference(cell(row, single[ROLE.PaymentReference])),
      earnings, deductions, taxes,
    });
  }

  if (skipped > 0) {
    issues.push({ severity: 'Warning', code: 'ROWS_SKIPPED_NO_EMPLOYEE', message: `${skipped} row(s) have no employee ID (for example total or blank rows) and were skipped.`, file: null, rowNumber: null, sourceRecordId: null });
  }
  if (!statedNetMapped) {
    issues.push({
      severity: 'Warning', code: 'RECONCILIATION_NOT_POSSIBLE',
      message: 'No net pay column was chosen, so EPN cannot confirm that your file\'s take-home pay matches the earnings, taxes and deductions. Review the totals carefully.',
      file: null, rowNumber: null, sourceRecordId: null,
    });
  }
  issues.push(
    { severity: 'Information', code: 'EMPLOYMENT_STATUS_NOT_IN_FILE', message: 'Employment status is not part of this kind of file; employees are listed as Active for review only.', file: null, rowNumber: null, sourceRecordId: null },
    { severity: 'Information', code: 'VOIDS_NOT_IN_GUIDED_MAPPING', message: 'Voids and corrections cannot be described in a guided-mapping file; use the EPN Standard template for those.', file: null, rowNumber: null, sourceRecordId: null },
  );

  const runData: MockRunData[] = [...runs.values()].map((r) => {
    const run: PreviewRun = {
      sourcePayrollRunId: r.id, periodStart: r.start, periodEnd: r.end, payDate: r.pay, payrollType: 'Regular', runStatus: 'Completed',
      employeeCount: new Set(r.statements.map((s) => s.sourceEmployeeId)).size,
      grossPay: sumMoney(r.statements.map((s) => s.grossPay)),
      employeeTaxes: sumMoney(r.statements.map((s) => s.employeeTaxes)),
      employeeDeductions: sumMoney(r.statements.map((s) => s.preTaxDeductions + s.postTaxDeductions)),
      employerTaxes: sumMoney(r.statements.map((s) => s.employerTaxes)),
      netPay: sumMoney(r.statements.map((s) => s.netPay)),
      voidedStatementCount: 0, correctedStatementCount: 0, alreadyImported: false,
    };
    return { run, statements: r.statements };
  });

  return {
    employees: [...employees.values()], runs: runData, issues,
    earningCodes: [...earningCodes].sort(), deductionCodes: [...deductionCodes].sort(), taxJurisdictions: [...jurisdictions].sort(),
  };
}

/** The built runs with each statement's EPN employee filled in from the current matches. */
export function guidedRuns(build: GuidedBuild, epnIdFor: (sourceEmployeeId: string) => string | null): MockRunData[] {
  return build.runs.map(({ run, statements }) => ({
    run, statements: statements.map((s) => ({ ...s, epnEmployeeId: epnIdFor(s.sourceEmployeeId) })),
  }));
}
